// src/pages/InsightsAndAnalytics/NoServicesConnected.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import noserviceTab1 from '@assets/images/noservice-tab-1.jpg';
import noserviceTab2 from '@assets/images/noservice-tab-2.jpg';
import noserviceTab3 from '@assets/images/noservice-tab-3.jpg';
import noserviceTab4 from '@assets/images/noservice-tab-4.jpg';
import noserviceHeader from '@assets/images/noservice-header.png';
import { useSendNoServicesConnectedEmail } from '@hooks/connectedServices';
import useUIStore from '@store/uiStore';
import { Variant } from '@components/ui/Toast';
import useAuthStore from '@store/authStore';
import { UserRoles } from '@hooks/users/types';
import { usePageHeader } from '../../context/PageHeaderContext';

type TabType = 'utilisation' | 'adoption' | 'security' | 'control';

interface TabContent {
  title: string;
  items: string[];
  images: string[];
}

const tabContents: Record<TabType, TabContent> = {
  utilisation: {
    title: 'Utilisation & Optimisation',
    items: [
      'Take control of your licenses, devices, and digital overheads.',
      'Spot unassigned, unused, or duplicated licenses.',
      'Identify renewal timelines and overspend risks.',
      'Understand what\'s deployed – and what\'s just sitting idle.',
      'Uncover savings without affecting productivity.',
    ],
    images: [
      noserviceTab1
    ],
  },
  adoption: {
    title: 'Adoption & Productivity',
    items: [
      'See what\'s being used, how often, and by who.',
      'Track engagement with tools like Teams, SharePoint, meeting room devices and more.',
      'Flag underused features or teams that may need training.',
      'Compare usage across departments to focus support and simplify where needed.',
    ],
    images: [
      noserviceTab2
    ],
  },
  security: {
    title: 'Security & Assurance',
    items: [
      'Shine a light on risks, threats, and blind spots.',
      'Monitor MFA coverage and failed logins globally.',
      'Identify shadow IT and third-party apps with risky permissions.',
      'Flag unlicensed accounts, shared mailboxes, and suspicious activity.',
      'Strengthen your Microsoft 365 security posture, fast.',
    ],
    images: [
      noserviceTab3
    ],
  },
  control: {
    title: 'Control & Governance',
    items: [
      'Keep your digital estate clean, compliant, and under control.',
      'Understand the volume and ownership of Teams groups.',
      'Track SharePoint and cloud storage usage.',
      'See where governance is missing and resolve it before it becomes a problem.',
    ],
    images: [
      noserviceTab4
    ],
  },
};

export const NoServicesConnected: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('utilisation');
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const { addToast } = useUIStore();
  const sendEmailMutation = useSendNoServicesConnectedEmail();
  const { setCustomTitle } = usePageHeader();

  const userRole = useAuthStore((state) => state.role);
  const userId = useAuthStore((state) => state.user?.id);
  const isAdminOrSuperAdmin = userRole === UserRoles.ADMIN || userRole === UserRoles.SUPERADMIN || userRole === UserRoles.SUPPORT;
  
  // Hide header for this full-page experience
  useEffect(() => {
    setCustomTitle(null);
    return () => {
      setCustomTitle(undefined);
    };
  }, [setCustomTitle]);
  
  const getSupportResponseTime = () => {
    try {
      const config = sessionStorage.getItem('client-configuration');
      if (config) {
        const parsed = JSON.parse(config);
        return parsed.supportResponseTime || '36 hours';
      }
    } catch (error) {
      console.error('Failed to parse client-configuration', error);
    }
    return '36 hours';
  };
  const supportResponseTime = getSupportResponseTime();

  // Check localStorage on mount to see if email was already sent for this user
  useEffect(() => {
    const storageKey = `noservices_email_sent_${userId}`;
    const emailSentStatus = localStorage.getItem(storageKey);
    if (emailSentStatus === 'true') {
      setEmailSent(true);
    }
  }, [userId]);

  const handleConnectServices = () => {
    navigate('/settings/manage-connected-services');
  };
  
  const handleGetHelp = async () => {
    try {
      await sendEmailMutation.mutateAsync();
      
      // Store in localStorage that email was sent for this user
      const storageKey = `noservices_email_sent_${userId}`;
      localStorage.setItem(storageKey, 'true');
      setEmailSent(true);
      
      addToast({
        variant: Variant.Success,
        message: 'The support team has been notified; you will be contacted soon.',
        closeable: true,
      });
    } catch (error) {
      addToast({
        variant: Variant.Error,
        message: "Something went wrong and the account owner wasn't notified. Please email support@cloudclevr.com.",
        closeable: true,
      });
    }
  };

  const currentContent = tabContents[activeTab];

  return (
    <div className="relative -mt-5 overflow-hidden"> 
    <div className="mb-16 rounded-lg bg-white p-0 shadow-md">
        
      {/* Header Section with full-width background */}
      <div 
        className="relative w-full bg-no-repeat"
        style={{ 
          backgroundColor: '#F8F9FA',
          backgroundImage: `url(${noserviceHeader})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          minHeight: '520px',
        }}
      >
        {/* Header Content */}
        <div className="relative text-center px-4 py-5 pt-20">
          <h1 className="pt-20 text-[64px] font-normal leading-[1.2] text-gray-900 md:text-[64px]" data-testid="no-services-heading">
            Unlock the power of your
            <br />
            Microsoft 365 estate
          </h1>
          <p className="mt-10 text-[36px] text-gray-800">
            Connect now to activate real-time insights with Clevr360.
          </p>
          
        </div>
      </div>
      <div className="w-full px-0 py-0 text-center -mt-8">
        {isAdminOrSuperAdmin && (
            <div className="mt-6 pt-3">
                <button
                data-testid="connect-services-button-header"
                onClick={handleConnectServices}
                className="inline-flex items-center justify-center rounded-full bg-[#00EC7B] px-9 py-3.5 text-base font-medium text-gray-900 shadow-sm transition-all hover:bg-[#02ce6b] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2"
                >
                Connect your services
                </button>
            </div>
            )}
      </div>
      
      {/* Two Column Info Section */}
        <div className="relative px-9 py-10 grid gap-6 text-left md:grid-cols-2 md:gap-10">
            {/* Left Column */}
            <div className="space-y-5">
              <p className="text-16 leading-relaxed text-gray-800 pl-12">
                Before we can show you what&apos;s really going on across your Microsoft 365 environment, you need to switch us on. Once connected, Clevr360 turns your invisible inefficiencies into clear opportunities - across cost, compliance, security, and productivity.
              </p>
              <p className="text-16 leading-relaxed text-gray-800 pl-12">
                Connect Microsoft 365 today to get access to four powerful insight categories, designed to help IT leaders cut through the noise.
              </p>
            </div>

            {/* Vertical Separator */}
            <div className="absolute left-1/2 top-0 hidden h-[60%] mt-10 w-px -translate-x-1/2 bg-gray-200 md:block" />

            {/* Right Column */}
            <div className="relative pl-5">
              <h2 className="mb-5 text-base font-semibold text-gray-900">
                Why wait? Connect now to get:
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <svg
                    className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <circle cx="12" cy="12" r="10" strokeWidth={2} />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M8 12.5l3 3 5-6"
                    />
                  </svg> 
                  <span className="text-base leading-relaxed text-gray-800">
                    Real insights in minutes
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <svg
                    className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <circle cx="12" cy="12" r="10" strokeWidth={2} />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M8 12.5l3 3 5-6"
                    />
                  </svg>
                  <span className="text-base leading-relaxed text-gray-800">
                    Better control, better decisions, better outcomes
                  </span>
                </li>
              </ul>
            </div>
          </div>

    </div>         
      {/* Main content with margins */}
      <div className="relative mr-0 ml-0  bg-[#F8F9FA]">
        <div className="w-full px-0 py-0">

        {/* Insights Section - keeping the same */}
        <div className="mb-16 rounded-lg bg-white px-12 py-12 shadow-lg">
            <h2 className="mb-5 text-center text-28 font-normal text-gray-900">
              No more guesswork. No more waste. Just insight-driven action.
            </h2>

            {/* Tabs */}
            <div data-testid="insights-tabs" className="mb-12 flex flex-wrap justify-center gap-3 bg-[#F4F4F5] rounded-full px-2 py-2 mx-auto max-w-fit">
              <button
                data-testid="tab-utilisation"
                onClick={() => setActiveTab('utilisation')}
                className={`rounded-full px-6 py-2.5 text-base font-medium transition-all ${
                  activeTab === 'utilisation'
                    ? 'bg-white text-gray-900 shadow-sm font-semibold'
                    : 'bg-transparent text-gray-600 hover:text-gray-900 font-semibold'
                }`}
              >
                <span data-testid="tab-Utilisation">Utilisation & optimisation</span>
              </button>
              <button
                data-testid="tab-adoption"
                onClick={() => setActiveTab('adoption')}
                className={`rounded-full px-6 py-2.5 text-base font-medium transition-all ${
                  activeTab === 'adoption'
                    ? 'bg-white text-gray-900 shadow-sm font-semibold'
                    : 'bg-transparent text-gray-600 hover:text-gray-900 font-semibold'
                }`}
              >
                <span data-testid="tab-Adoption">Adoption & productivity</span>
              </button>
              <button
                data-testid="tab-security"
                onClick={() => setActiveTab('security')}
                className={`rounded-full px-6 py-2.5 text-base font-medium transition-all ${
                  activeTab === 'security'
                    ? 'bg-white text-gray-900 shadow-sm font-semibold'
                    : 'bg-transparent text-gray-600 hover:text-gray-900 font-semibold'
                }`}
              >
                <span data-testid="tab-Assurance">Security & assurance</span>
              </button>
              <button
                data-testid="tab-control"
                onClick={() => setActiveTab('control')}
                className={`rounded-full px-6 py-2.5 text-base font-medium transition-all ${
                  activeTab === 'control'
                    ? 'bg-white text-gray-900 shadow-sm font-semibold'
                    : 'bg-transparent text-gray-600 hover:text-gray-900 font-semibold'
                }`}
              >
                Control & governance
              </button>
            </div>

            {/* Tab Content - Two Column Layout */}
            <div data-testid="tab-content" className="grid gap-16 md:grid-cols-2 md:gap-20 mb-10">
              {/* Left Column - Text Content */}
              <div data-testid={`tab-content-${activeTab}`} className="flex flex-col justify-center ml-5">
                <h3 className="mb-6 text-28 font-normal text-gray-900">
                  {currentContent.title}
                </h3>
                <ul className="space-y-4">
                  {currentContent.items.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <svg
                        className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <circle cx="12" cy="12" r="10" strokeWidth={2} />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d="M8 12.5l3 3 5-6"
                        />
                      </svg>
                      <span className="text-[16px] leading-relaxed text-gray-800">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Right Column - Dashboard Preview Images */}
              <div className="relative mr-4">
                {/* Images Grid - Single image per tab */}
                <div className="overflow-hidden rounded-lg">
                  <img
                    src={currentContent.images[0]}
                    alt={`${currentContent.title} preview`}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      console.error('Image failed to load:', currentContent.images[0]);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom CTA Section */}
      <div className="relative z-10 bg-slate-800 px-4 py-10 rounded-md">
        <div className="mx-auto flex w-full pl-5 pr-5 flex-col items-center justify-between gap-8 md:flex-row">
          <div className="flex-1">
            <h2 className="mb-2 text-20 font-normal text-white">
              Clear insights. Real outcomes. Smarter decisions. That&apos;s Clevr.
            </h2>
            <p className="mb-3 text-16 text-gray-300">
              Connect Microsoft 365 and RingCentral today, and bring clarity to your digital estate.
            </p>
            {isAdminOrSuperAdmin && (
              emailSent ? (
                <div data-testid="email-sent-success-panel" className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 p-4">
                  <div className="flex items-start gap-3">
                    <svg
                      className="h-6 w-6 flex-shrink-0 text-emerald-500 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <circle cx="12" cy="12" r="10" strokeWidth={2} />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4"
                      />
                    </svg>
                    <div>
                      <h3 className="text-base font-semibold text-emerald-900 mb-1">
                        You&apos;ve started the process to get your services connected.
                      </h3>
                      <p className="text-sm text-emerald-800">
                        One of our team will be in touch about what happens next within {supportResponseTime}.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  data-testid="get-help-button"
                  onClick={handleGetHelp}
                  disabled={sendEmailMutation.isPending}
                  className="inline-flex items-center gap-2 text-16 text-emerald-400 hover:text-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-none cursor-pointer p-0"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"
                    />
                    <circle cx="12" cy="17" r="0.5" fill="currentColor" />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 21l2-2"
                    />
                  </svg>
                  {sendEmailMutation.isPending ? 'Sending an email to support...' : 'Get help connecting your services'}
                </button>
              )
            )}
          </div>
          {isAdminOrSuperAdmin && (
            <div className="pr-5">          
                <button
                data-testid="connect-services-button-footer"
                onClick={handleConnectServices}
                className="inline-flex items-center justify-center rounded-full bg-[#00EC7B] px-9 py-3.5 text-base text-gray-900 shadow-sm transition-all hover:bg-[#02ce6b] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2"                
                >
                Connect your services
                </button>
            </div>
            )}
        </div>
      </div>
    </div>
  );
};